import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { LogOut, Trash2, X, Heart, ChevronDown, Crown, User, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getClassificationColor, getClassificationForElo } from '../utils/classificationUtils';
import SetProfileModal from './SetProfileModal';

interface UserMenuProps {
  onOpenLogin: () => void;
  currentLang: 'en' | 'fr' | 'de' | 'it';
}

export const UserMenu: React.FC<UserMenuProps> = ({ onOpenLogin, currentLang }) => {
  const { user, logout, deleteAccount, favorites, removeFavorite, isPro, userPreferences } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSetProfileModal, setShowSetProfileModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  // Extract my profile data from userPreferences
  const myProfile = userPreferences?.my_player_link ? {
    playerLink: userPreferences.my_player_link,
    name: userPreferences.my_player_name || '',
    elo: userPreferences.my_player_elo,
    classification: userPreferences.my_player_classification || (userPreferences.my_player_elo ? getClassificationForElo(userPreferences.my_player_elo) : undefined),
    avatar: userPreferences.profile_avatar || 'profile_guy'
  } : null;

  // Log Pro status for debugging
  React.useEffect(() => {
    if (user) {
      console.log('👤 User Menu - User:', user.email, '| Pro Status:', isPro ? '👑 PRO' : '🆓 FREE');
    }
  }, [user, isPro]);

  const translations = {
    en: {
      login: 'Login',
      signedInAs: 'Signed in as',
      favorites: 'favorites',
      myFavorites: 'My Favorites',
      myProfile: 'My Profile',
      viewProfile: 'View Profile',
      changeProfile: 'Change Profile',
      setProfile: 'Set Profile',
      noFavorites: 'No favorites yet',
      getPro: 'Get Pro',
      logout: 'Log Out',
      deleteAccount: 'Delete Account',
      deleteModalTitle: 'Delete Account?',
      deleteModalMessage: 'Are you sure you want to permanently delete your account? This will remove all your favorites and cannot be undone.',
      cancel: 'Cancel',
      confirmDelete: 'Delete Account',
    },
    fr: {
      login: 'Connexion',
      signedInAs: 'Connecté en tant que',
      favorites: 'favoris',
      myFavorites: 'Mes Favoris',
      myProfile: 'Mon Profil',
      viewProfile: 'Voir le profil',
      changeProfile: 'Changer de profil',
      setProfile: 'Définir le profil',
      noFavorites: 'Aucun favori',
      getPro: 'Passer Pro',
      logout: 'Déconnexion',
      deleteAccount: 'Supprimer le compte',
      deleteModalTitle: 'Supprimer le compte ?',
      deleteModalMessage: 'Es-tu sûr de vouloir supprimer définitivement ton compte ? Cela supprimera tous tes favoris et ne peut pas être annulé.',
      cancel: 'Annuler',
      confirmDelete: 'Supprimer le compte',
    },
    de: {
      login: 'Anmelden',
      signedInAs: 'Angemeldet als',
      favorites: 'Favoriten',
      myFavorites: 'Meine Favoriten',
      myProfile: 'Mein Profil',
      viewProfile: 'Profil anzeigen',
      changeProfile: 'Profil ändern',
      setProfile: 'Profil festlegen',
      noFavorites: 'Keine Favoriten',
      getPro: 'Pro holen',
      logout: 'Abmelden',
      deleteAccount: 'Konto löschen',
      deleteModalTitle: 'Konto löschen?',
      deleteModalMessage: 'Bist du sicher, dass du dein Konto dauerhaft löschen möchtest? Dies entfernt alle deine Favoriten und kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      confirmDelete: 'Konto löschen',
    },
    it: {
      login: 'Accedi',
      signedInAs: 'Connesso come',
      favorites: 'preferiti',
      myFavorites: 'I Miei Preferiti',
      myProfile: 'Il Mio Profilo',
      viewProfile: 'Vedi profilo',
      changeProfile: 'Cambia profilo',
      setProfile: 'Imposta profilo',
      noFavorites: 'Nessun preferito',
      getPro: 'Passa a Pro',
      logout: 'Disconnetti',
      deleteAccount: 'Elimina account',
      deleteModalTitle: 'Eliminare l\'account?',
      deleteModalMessage: 'Sei sicuro di voler eliminare definitivamente il tuo account? Questo rimuoverà tutti i tuoi preferiti e non può essere annullato.',
      cancel: 'Annulla',
      confirmDelete: 'Elimina account',
    },
  };

  const t = translations[currentLang] || translations.en;

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      await deleteAccount();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting account:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) {
    return (
      <motion.button
        onClick={onOpenLogin}
        className="px-2 py-1 rounded text-xs font-medium bg-[#1C7AD6] dark:bg-[#1C7AD6] text-white hover:bg-[#1565C0] dark:hover:bg-[#1565C0] transition-all duration-300"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {t.login}
      </motion.button>
    );
  }

  return (
    <>
      {/* Menu Button */}
      <button
        onClick={() => setShowMobileMenu(true)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <User className="w-4 h-4" />
        {isPro && <Crown className="w-3 h-3 text-[#F59E0B]" />}
        <ChevronDown className="w-3 h-3" />
      </button>

      {/* Fullscreen Menu - Rendered as Portal */}
      {createPortal(
        <AnimatePresence>
          {showMobileMenu && (
            <>
              {/* Backdrop */}
              <motion.div
                className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm z-[100] transition-colors duration-300"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowMobileMenu(false)}
              />

              {/* Centered Modal */}
              <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
                <motion.div
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transition-colors duration-300"
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 px-6 py-5 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">{t.signedInAs}</p>
                          {isPro && (
                            <span className="flex items-center gap-1 px-2 py-0.5 bg-[#F59E0B] dark:bg-[#D97706] text-white rounded-md text-xs font-bold transition-colors duration-300">
                              <Crown className="w-3 h-3" />
                              PRO
                            </span>
                          )}
                        </div>
                        <p className="text-base font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">{user.email}</p>
                      </div>
                      <button
                        onClick={() => setShowMobileMenu(false)}
                        className="p-2 rounded-lg hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-colors duration-300"
                      >
                        <X className="w-5 h-5 text-gray-500 dark:text-gray-400 transition-colors duration-300" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
                    {/* My Profile Section */}
                    {myProfile && (
                      <div className="mb-6">
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 transition-colors duration-300">{t.myProfile}</p>
                        <button
                          onClick={() => {
                            navigate(`/${currentLang}/player/${encodeURIComponent(myProfile.playerLink)}`);
                            setShowMobileMenu(false);
                          }}
                          className="relative flex items-center gap-3 w-full p-3 rounded-xl bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors duration-300 group"
                        >
                          <User size={16} className="text-gray-500 dark:text-gray-400 flex-shrink-0 transition-colors duration-300" />
                          <div className="flex-1 flex items-center gap-2 text-left min-w-0">
                            <span className="text-sm text-gray-900 dark:text-gray-100 group-hover:text-[#1C7AD6] dark:group-hover:text-[#60A5FA] truncate font-medium flex-shrink transition-colors duration-300">
                              {myProfile.name.includes(',')
                                ? `${myProfile.name.split(',')[1]?.trim()} ${myProfile.name.split(',')[0]?.trim()}`
                                : myProfile.name}
                            </span>
                            {myProfile.classification && (() => {
                              const colors = getClassificationColor(myProfile.classification);
                              return (
                                <span
                                  className="text-xs font-bold px-2 py-0.5 rounded-lg whitespace-nowrap flex-shrink-0 text-white"
                                  style={{ backgroundColor: colors.bg === '#F3F4F6' ? '#1A1A1A' : colors.bg }}
                                >
                                  {myProfile.classification}
                                </span>
                              );
                            })()}
                            {myProfile.elo && (
                              <span className="text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-lg whitespace-nowrap flex-shrink-0 transition-colors duration-300">
                                {myProfile.elo}
                              </span>
                            )}
                          </div>
                        </button>
                      </div>
                    )}

                    {/* Favorites Section */}
                    <div className="mb-6">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 transition-colors duration-300">{t.myFavorites} ({favorites.length}/3)</p>
                      {favorites.length > 0 ? (
                        <div className="space-y-2">
                          {favorites.map((fav) => {
                            const classification = fav.classification;
                            const colors = classification ? getClassificationColor(classification) : null;

                            return (
                              <div
                                key={fav.link}
                                className="relative flex items-center gap-3 w-full p-3 rounded-xl bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors duration-300 group"
                              >
                                <Heart size={16} className="text-red-500 dark:text-red-400 flex-shrink-0 transition-colors duration-300" fill="currentColor" />

                                <button
                                  onClick={() => {
                                    navigate(`/${currentLang}/player/${encodeURIComponent(fav.link)}`);
                                    setShowMobileMenu(false);
                                  }}
                                  className="flex-1 flex items-center gap-2 text-left min-w-0"
                                >
                                  <span className="text-sm text-gray-900 dark:text-gray-100 group-hover:text-[#1C7AD6] dark:group-hover:text-[#60A5FA] truncate font-medium flex-shrink transition-colors duration-300">
                                    {fav.name}
                                  </span>

                                  {colors && (
                                    <span
                                      className="text-xs font-bold px-2 py-0.5 rounded-lg whitespace-nowrap flex-shrink-0"
                                      style={{
                                        backgroundColor: colors.bg,
                                        color: colors.text
                                      }}
                                    >
                                      {classification}
                                    </span>
                                  )}

                                  {fav.elo && (
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-lg whitespace-nowrap flex-shrink-0 transition-colors duration-300">
                                      {fav.elo}
                                    </span>
                                  )}
                                </button>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFavorite(fav.link);
                                  }}
                                  className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-300"
                                  title="Remove from favorites"
                                >
                                  <Trash2 size={14} className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-300" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Heart size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-2 transition-colors duration-300" />
                          <p className="text-sm text-gray-400 dark:text-gray-500 transition-colors duration-300">{t.noFavorites}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="space-y-2 pt-4 border-t border-gray-100 dark:border-gray-700 transition-colors duration-300">
                      {/* Get Pro Button (only for non-Pro users) */}
                      {!isPro && (
                        <button
                          onClick={() => {
                            navigate(`/${currentLang}/pro`);
                            setShowMobileMenu(false);
                          }}
                          className="flex items-center gap-3 w-full p-3 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors duration-300 group"
                        >
                          <Crown size={18} className="text-yellow-600 dark:text-yellow-400 transition-colors duration-300" />
                          <span className="text-sm text-yellow-700 dark:text-yellow-400 font-medium transition-colors duration-300">{t.getPro}</span>
                        </button>
                      )}

                      <button
                        onClick={() => {
                          setShowMobileMenu(false);
                          setShowSetProfileModal(true);
                        }}
                        className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-300 group"
                      >
                        <Search size={18} className="text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200 transition-colors duration-300" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 font-medium transition-colors duration-300">
                          {myProfile ? t.changeProfile : t.setProfile}
                        </span>
                      </button>

                      <button
                        onClick={() => {
                          logout();
                          setShowMobileMenu(false);
                        }}
                        className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-300 group"
                      >
                        <LogOut size={18} className="text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200 transition-colors duration-300" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 font-medium transition-colors duration-300">{t.logout}</span>
                      </button>

                      <button
                        onClick={() => {
                          setShowMobileMenu(false);
                          setShowDeleteModal(true);
                        }}
                        className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-300 group"
                      >
                        <Trash2 size={18} className="text-red-500 dark:text-red-400 group-hover:text-red-600 dark:group-hover:text-red-500 transition-colors duration-300" />
                        <span className="text-sm text-red-600 dark:text-red-400 group-hover:text-red-700 dark:group-hover:text-red-500 font-medium transition-colors duration-300">{t.deleteAccount}</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Delete Confirmation Modal - Rendered as Portal */}
      {createPortal(
        <AnimatePresence>
          {showDeleteModal && (
            <>
              {/* Backdrop */}
              <motion.div
                className="fixed inset-0 bg-black/30 dark:bg-black/50 backdrop-blur-sm z-[100] transition-colors duration-300"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => !isDeleting && setShowDeleteModal(false)}
              />

              {/* Dialog */}
              <motion.div
                className="fixed inset-0 z-[101] flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => !isDeleting && setShowDeleteModal(false)}
              >
                <motion.div
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden w-full max-w-md transition-colors duration-300"
                  initial={{ scale: 0.9, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 20 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="relative bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">
                      {t.deleteModalTitle}
                    </h3>
                    <button
                      onClick={() => !isDeleting && setShowDeleteModal(false)}
                      disabled={isDeleting}
                      className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-colors duration-300 disabled:opacity-50"
                    >
                      <X className="h-4 w-4 text-gray-500 dark:text-gray-400 transition-colors duration-300" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="px-6 py-5">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 transition-colors duration-300">
                      {t.deleteModalMessage}
                    </p>
                    <div className="flex justify-end gap-3">
                      <motion.button
                        onClick={() => setShowDeleteModal(false)}
                        disabled={isDeleting}
                        className="px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors duration-300 disabled:opacity-50"
                        whileHover={{ scale: isDeleting ? 1 : 1.02 }}
                        whileTap={{ scale: isDeleting ? 1 : 0.98 }}
                      >
                        {t.cancel}
                      </motion.button>
                      <motion.button
                        onClick={handleDeleteAccount}
                        disabled={isDeleting}
                        className="px-4 py-2.5 bg-red-500 dark:bg-red-600 hover:bg-red-600 dark:hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors duration-300 disabled:opacity-50"
                        whileHover={{ scale: isDeleting ? 1 : 1.02 }}
                        whileTap={{ scale: isDeleting ? 1 : 0.98 }}
                      >
                        {isDeleting ? 'Deleting...' : t.confirmDelete}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Set Profile Modal */}
      <SetProfileModal
        isOpen={showSetProfileModal}
        onClose={() => setShowSetProfileModal(false)}
        lang={currentLang}
      />
    </>
  );
};
